import { type QueryClient, useQueryClient } from "@tanstack/solid-query";
import { createFileRoute, useNavigate } from "@tanstack/solid-router";
import { For, Show } from "solid-js";
import {
  IconBot,
  IconBrandTelegram,
  IconMessageSquare,
} from "~/components/icons";
import { Badge } from "~/components/ui/badge";
import {
  chatSessionResource,
  chatSessionsResource,
} from "~/features/chat/chat.resource";
import { formatDisplayDateTime } from "~/lib/utils";

export const Route = createFileRoute("/_dashboard/dashboard/chat/")({
  loader: async ({ context }: { context: { queryClient: QueryClient } }) => {
    const sessions = await context.queryClient.ensureQueryData(
      chatSessionsResource.options(undefined),
    );
    return { sessions };
  },
  component: ChatHistoryPage,
});

function ChatHistoryPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const data = Route.useLoaderData();

  type SessionItem = {
    id: string;
    title: string;
    source: string;
    updatedAt: string | Date;
  };

  const sessionsQuery = chatSessionsResource.use(() => undefined);
  const sessions = (): SessionItem[] =>
    (sessionsQuery.data ?? data().sessions) as SessionItem[];
  const telegramSessions = () =>
    sessions().filter((session) => session.source === "telegram");
  const webSessions = () =>
    sessions().filter((session) => session.source !== "telegram");

  const prefetchSession = (sessionId: string) => {
    void qc.prefetchQuery(chatSessionResource.options(sessionId));
  };

  chatSessionsResource.seedIfEmpty(qc, undefined, data().sessions);

  const renderSessionCard = (session: SessionItem) => (
    <button
      type="button"
      onPointerEnter={() => prefetchSession(session.id)}
      onFocus={() => prefetchSession(session.id)}
      onTouchStart={() => prefetchSession(session.id)}
      onMouseDown={() => prefetchSession(session.id)}
      onClick={() =>
        void navigate({
          to: "/dashboard/chat/$sessionId",
          params: { sessionId: session.id },
        })
      }
      class="flex items-start justify-between rounded-lg border bg-card p-4 text-left hover:bg-accent transition-colors"
    >
      <div class="flex flex-col gap-1 min-w-0">
        <span class="font-medium truncate">{session.title}</span>
        <span class="text-xs text-muted-foreground">
          {formatDisplayDateTime(session.updatedAt) ?? "-"}
        </span>
      </div>
      <Badge
        variant={session.source === "telegram" ? "secondary" : "outline"}
        class="ml-4 shrink-0 capitalize"
      >
        {session.source}
      </Badge>
    </button>
  );

  return (
    <div class="p-6 max-w-3xl mx-auto">
      <div class="flex items-center gap-3 mb-6">
        <IconMessageSquare class="size-6 text-muted-foreground" />
        <h1 class="text-2xl font-semibold">Chat History</h1>
      </div>

      <Show
        when={sessions().length > 0}
        fallback={
          <div class="flex flex-col items-center justify-center py-16 text-muted-foreground gap-3">
            <IconBot class="size-10 opacity-40" />
            <p class="text-sm">No chat sessions yet.</p>
          </div>
        }
      >
        <div class="flex flex-col gap-5">
          <Show when={webSessions().length > 0}>
            <section class="flex flex-col gap-2">
              <div class="flex items-center gap-2 px-1 text-xs uppercase tracking-wide text-muted-foreground">
                <IconBot class="size-3.5" />
                <span>Agent Chats</span>
                <span>({webSessions().length})</span>
              </div>
              <For each={webSessions()}>
                {(session) => renderSessionCard(session)}
              </For>
            </section>
          </Show>

          <Show when={telegramSessions().length > 0}>
            <section class="flex flex-col gap-2">
              <div class="flex items-center gap-2 px-1 text-xs uppercase tracking-wide text-muted-foreground">
                <IconBrandTelegram class="size-3.5 text-[#2AABEE]" />
                <span>Telegram Chats</span>
                <span>({telegramSessions().length})</span>
              </div>
              <For each={telegramSessions()}>
                {(session) => renderSessionCard(session)}
              </For>
            </section>
          </Show>
        </div>
      </Show>
    </div>
  );
}
